import { Yup } from 'cng-web-lib'
import Namespace from 'src/constants/locale/Namespace'
import { constants } from 'cng-web-lib'
import UaeMpciKeys from 'src/constants/locale/key/UaeMpci'

const {
  locale: {
    key: { CommonValidationMessageKeys }
  }
} = constants

function makeValidationSchema(translate) {
  let requiredMessage = translate
    ? translate(Namespace.COMMON_VALIDATION_MSG, CommonValidationMessageKeys.REQUIRED)
    : 'Required'
  let dateTypeErrorMessage = translate
    ? translate(Namespace.COMMON_VALIDATION_MSG, CommonValidationMessageKeys.DATE_TYPE_ERROR)
    : 'Invalid date'

  const errMsgAlphaNumericWithSpecialChar = translate
    ? translate(Namespace.UAE_MPCI, UaeMpciKeys.ValidationMessage.ERROR_MSG_ALPHA_NUMERIC_WITH_SPECIAL_CHAR)
    : "Allowed special characters are . , - ( ) / = ? * & @ # ! _ ; \" % $ ^ \\ | < > { } [ ] ~"
  const errMsgAlphaNumericWithSpecialCharNoSpace = translate
    ? translate(Namespace.UAE_MPCI, UaeMpciKeys.ValidationMessage.ERROR_MSG_ALPHA_NUMERIC_WITH_SPECIAL_CHAR_NO_SPACE)
    : "Allowed special characters are . , - ( ) / = ? * & @ # ! _ ; \" % $ ^ \\ | < > { } [ ] ~ (no spaces)"

  const regexAlphaNumericWithSpecialChar =
    '^[a-zA-Z0-9 .,\\-()/=?*&@#!_;"% $^\\\\|<>{}\\[\\]~]*$'
  const regexAlphaNumericWithSpecialCharNoSpace =
    '^[a-zA-Z0-9.,\\-()/=?*&@#!_;"%$^\\\\|<>{}\\[\\]~]*$'
  const regexNumeric = '^[0-9]*$'

  function isVesselInfoTouched(parent) {
    return !!(
      parent.vesselName ||
      parent.voyageNumber ||
      parent.carrierIdentifier ||
      parent.imoNumber ||
      parent.vesselFlagCountry ||
      parent.vesselOwnershipIndicatorCode ||
      parent.vesselPowerTypeCode ||
      parent.vesselPowerTypeDescription ||
      parent.vesselLastForeignPortOfDeparture ||
      parent.vesselFirstUaePortOfArrival ||
      parent.etdAtTheLastForeignPort ||
      parent.atdAtTheLastForeignPort ||
      parent.etaAtTheFirstUaeSeaport
    )
  }

  return Yup.object({
        // Step 1: General Information
    filingOperator: Yup.string().nullable().test('required', requiredMessage, val => !!val),
    documentType: Yup.string().nullable().test('required', requiredMessage, val => !!val),
    processingIndicator: Yup.string().nullable().test('required', requiredMessage, val => !!val),
    shipmentType: Yup.string().nullable().test('required', requiredMessage, val => !!val),
    parentBillNo: Yup.string().nullable()
      .test('required', requiredMessage, val => !!val)
      .matches(regexAlphaNumericWithSpecialCharNoSpace, errMsgAlphaNumericWithSpecialCharNoSpace)
      .max(70, 'Max 70 characters'),
    billNo: Yup.string().nullable()
      .test('required', requiredMessage, val => !!val)
      .matches(regexAlphaNumericWithSpecialCharNoSpace, errMsgAlphaNumericWithSpecialCharNoSpace)
      .max(70, 'Max 70 characters'),
    totalNumberOfFfBillsAssociatedWithMasterOrParent: Yup.string()
      .required(requiredMessage)
      .matches(regexNumeric, 'Must be numeric')
      .max(18, 'Max 18 characters'),
    shippingLineMpciPartyId: Yup.string().nullable().test('required', requiredMessage, val => !!val),
    uaeFFSubColoaderMpciPartyId: Yup.string().nullable()
      .when('documentType', {
        is: '716',
        then: Yup.string().nullable().test('required', requiredMessage, val => !!val)
      }),
    uaeFreightForwarderMpciPartyId: Yup.string().nullable()
      .when('filingOperator', {
        is: 'DFF',
        then: Yup.string().nullable().test('required', requiredMessage, val => !!val)
      }),
    placeOfBillIssueCountry: Yup.string().nullable(),
    billIssueLocationName: Yup.string().nullable()
      .matches(regexAlphaNumericWithSpecialChar, errMsgAlphaNumericWithSpecialChar)
      .max(256, 'Max 256 characters'),
    bolIssueDate: Yup.lazy((value) =>
      value === '' || value === null
        ? Yup.string().nullable()
        : Yup.date().nullable().typeError(dateTypeErrorMessage)
    ),
    freightPayableCountry: Yup.string().nullable(),
    freightLocationName: Yup.string().nullable()
      .matches(regexAlphaNumericWithSpecialChar, errMsgAlphaNumericWithSpecialChar)
      .max(256, 'Max 256 characters'),
    sobDate: Yup.lazy((value) =>
      value === '' || value === null
        ? Yup.string().nullable()
        : Yup.date().nullable().typeError(dateTypeErrorMessage)
    ),
    shipmentDescription: Yup.string().nullable()
      .matches(regexAlphaNumericWithSpecialChar, errMsgAlphaNumericWithSpecialChar)
      .max(35, 'Max 35 characters'),
    processingIndicatorDescription: Yup.string().nullable()
      .matches(regexAlphaNumericWithSpecialChar, errMsgAlphaNumericWithSpecialChar)
      .max(256, 'Max 256 characters'),
    forwardingNotes: Yup.string().nullable()
      .matches(regexAlphaNumericWithSpecialChar, errMsgAlphaNumericWithSpecialChar)
      .max(512, 'Max 512 characters'),
    functionType: Yup.string().nullable(),
    reasonForUpdatingFiling: Yup.string().nullable()
      .matches(regexAlphaNumericWithSpecialChar, errMsgAlphaNumericWithSpecialChar)
      .max(512, 'Max 512 characters'),

    // Step 2: Vessel Information (optional section in HBL — inherited from master bill)
    // M fields are conditionally required: if ANY vessel info field is filled, all M fields must be present
    vesselName: Yup.string().nullable()
      .matches(regexAlphaNumericWithSpecialChar, errMsgAlphaNumericWithSpecialChar)
      .max(70, 'Max 70 characters')
      .test('vesselName-required', requiredMessage, function(val) {
        return !isVesselInfoTouched(this.parent) || !!val
      }),
    voyageNumber: Yup.string().nullable().max(17, 'Max 17 characters')
      .test('voyageNumber-required', requiredMessage, function(val) {
        return !isVesselInfoTouched(this.parent) || !!val
      }),
    carrierIdentifier: Yup.string().nullable()
      .test('carrierIdentifier-required', requiredMessage, function(val) {
        return !isVesselInfoTouched(this.parent) || !!val
      }),
    imoNumber: Yup.string().nullable()
      .test('imo-format', 'Must be exactly 7 digits', val => !val || /^[0-9]{7}$/.test(val)),
    vesselFlagCountry: Yup.string().nullable().max(2, 'Max 2 characters'),
    vesselOwnershipIndicatorCode: Yup.string().nullable(),
    vesselPowerTypeCode: Yup.string().nullable(),
    vesselPowerTypeDescription: Yup.string().nullable().max(17, 'Max 17 characters'),
    vesselLastForeignPortOfDeparture: Yup.string().nullable().max(6, 'Max 6 characters')
      .test('vesselLastForeignPort-required', requiredMessage, function(val) {
        return !isVesselInfoTouched(this.parent) || !!val
      }),
    vesselFirstUaePortOfArrival: Yup.string().nullable().max(6, 'Max 6 characters')
      .test('vesselFirstUaePort-required', requiredMessage, function(val) {
        return !isVesselInfoTouched(this.parent) || !!val
      }),
    etdAtTheLastForeignPort: Yup.lazy((value) =>
      value === '' || value === null
        ? Yup.string().nullable()
        : Yup.date().nullable().typeError(dateTypeErrorMessage)
    ),
    atdAtTheLastForeignPort: Yup.lazy((value) =>
      value === '' || value === null
        ? Yup.string().nullable()
        : Yup.date().nullable().typeError(dateTypeErrorMessage)
    ),
    etaAtTheFirstUaeSeaport: Yup.mixed().nullable()
      .test('eta-required', requiredMessage, function(val) {
        return !isVesselInfoTouched(this.parent) || !!val
      })
      .test('eta-date', dateTypeErrorMessage, val => !val || !isNaN(Date.parse(val))),

            // Step 2: Consignment sub-entities
    portOfLoading: Yup.string().nullable().test('required', requiredMessage, val => !!val).max(6, 'Max 6 characters'),
    portOfDischarge: Yup.string().nullable().test('required', requiredMessage, val => !!val).max(6, 'Max 6 characters'),
    placeOfReceiptCountry: Yup.string().nullable().test('required', requiredMessage, val => !!val),
    placeOfReceiptName: Yup.string().nullable().max(256, 'Max 256 characters'),
    placeOfDeliveryCountry: Yup.string().nullable().test('required', requiredMessage, val => !!val),
    placeOfDeliveryName: Yup.string().nullable().max(256, 'Max 256 characters'),
    emirate: Yup.string().nullable(),
    transshipmentPorts: Yup.array().of(
      Yup.object({
        unlocCode: Yup.string().nullable().max(6, 'Max 6 characters'),
        locationName: Yup.string().nullable().max(256, 'Max 256 characters')
      })
    ).nullable(),
    monetaryDetails: Yup.array().of(
      Yup.object({
        monetaryAmountType: Yup.string().nullable(),
        monetaryAmount: Yup.string().nullable()
          .test('monetary-format', 'Must be a number with up to 2 decimal places (e.g. 1250.50), max 33 digits', val => !val || /^[0-9]{1,31}(\.[0-9]{1,2})?$/.test(val))
          .max(34, 'Max 34 characters'),
        currencyCode: Yup.string().nullable(),
        chargeCategoryCode: Yup.string().nullable(),
        paymentArrangementCode: Yup.string().nullable()
      })
    ).nullable(),

    // Step 3: Parties
    partiesInfo: Yup.array().of(
      Yup.object({
        partyType: Yup.string().nullable(),
        partyIdentificationType: Yup.string().nullable()
          .when('partyType', {
            is: 'DDR',
            then: Yup.string().nullable().test('required', requiredMessage, val => !!val)
          }),
        partyId: Yup.string().nullable().max(35, 'Max 35 characters'),
        partyName: Yup.string().nullable().max(70, 'Max 70 characters')
          .when('partyType', {
            is: (type) => type && !['CE', 'CN'].includes(type),
            then: Yup.string().nullable().test('required', requiredMessage, val => !!val)
          }),
        address1: Yup.string().nullable().max(256, 'Max 256 characters'),
        address2: Yup.string().nullable().max(256, 'Max 256 characters'),
        address3: Yup.string().nullable().max(256, 'Max 256 characters'),
        city: Yup.string().nullable().max(35, 'Max 35 characters'),
        country: Yup.string().nullable().max(2, 'Max 2 characters'),
        establishmentOrIndividual: Yup.string().nullable()
          .when('partyType', {
            is: 'CN',
            then: Yup.string().nullable().test('required', requiredMessage, val => !!val)
          }),
        contactInfo: Yup.array().of(
          Yup.object({
            contactLevel: Yup.string().nullable().test('required', requiredMessage, val => !!val),
            contactName: Yup.string().nullable().max(256, 'Max 256 characters')
              .test('required', requiredMessage, val => !!val && val.trim() !== ''),
            telephoneNumber: Yup.string().nullable().max(512, 'Max 512 characters'),
            emailId: Yup.string().nullable().email('Invalid email').max(512, 'Max 512 characters'),
            url: Yup.string().nullable().max(512, 'Max 512 characters')
          })
        ).nullable()
      })
    ).nullable(),
    consignmentDetails: Yup.array().nullable(),

    // Step 4: Containers
    containerDetails: Yup.array().of(
      Yup.object({
                containerNumber: Yup.string().nullable().test('required', requiredMessage, val => !!val)
          .max(17, 'Max 17 characters'),
        containerSize: Yup.string().nullable().test('required', requiredMessage, val => !!val),
        containerLoadStatus: Yup.string().nullable().test('required', requiredMessage, val => !!val),
        sealIdentifier: Yup.string().nullable().test('required', requiredMessage, val => !!val)
          .max(35, 'Max 35 characters'),
        containerVerifiedGrossMass: Yup.string().nullable()
          .test('is-numeric', 'Must be a valid number', val => !val || /^[0-9]+(\.[0-9]+)?$/.test(val)),
        containerVerifiedGrossMassUnit: Yup.string().nullable(),
        temperatureType: Yup.string().nullable(),
        containerTemperatureDegree: Yup.string().nullable()
          .test('is-numeric', 'Must be a valid number', val => !val || /^-?[0-9]+(\.[0-9]+)?$/.test(val)),
        temperatureMeasurementCode: Yup.string().nullable()
          .test('temp-code-required', 'Required when Container Temperature Degree is filled', function(value) {
            return this.parent.containerTemperatureDegree ? !!value : true
          }),
        containerStatusDescription: Yup.string().nullable().max(35, 'Max 35 characters'),
        serviceRequirementCode: Yup.string().nullable(),
        cargoType: Yup.string().nullable(),
        sealingPartyCode: Yup.string().nullable(),
        sealingPartyName: Yup.string().nullable().max(35, 'Max 35 characters'),
        sealType: Yup.string().nullable()
      })
    ).nullable(),

    // Step 5: Goods
    goodsInfo: Yup.array().of(
      Yup.object({
        containerNumber: Yup.string().nullable().test('required', requiredMessage, val => !!val).max(17, 'Max 17 characters'),
        goodsItemNumber: Yup.string().nullable().max(6, 'Max 6 characters'),
        totalPackageQuantity: Yup.string().nullable()
          .test('required', requiredMessage, val => !!val)
          .test('is-int', 'Must be a whole number', val => !val || /^[0-9]+$/.test(val))
          .test('max-digits', 'Max 8 digits', val => !val || val.length <= 8),
        packageType: Yup.string().nullable().max(35, 'Max 35 characters'),
        packageTypeDescription: Yup.string().nullable().test('required', requiredMessage, val => !!val),
        packingDescription: Yup.string().nullable(),
        packingInstructionsCode: Yup.string().nullable(),
        hsCode: Yup.string().nullable().max(18, 'Max 18 characters')
          .test('hs-code-required', requiredMessage, function(val) {
            const root = this.from && this.from[this.from.length - 1]
            const documentType = root && root.value && root.value.documentType
            return documentType !== '714' || !!val
          }),
        goodsItemCountryOfOrigin: Yup.string().nullable().max(2, 'Max 2 characters'),
        goodsItemGrossWeight: Yup.string().nullable().test('required', requiredMessage, val => !!val)
          .test('is-whole', 'Must be a whole number (no decimals)', val => !val || /^[0-9]+$/.test(val))
          .max(18, 'Max 18 characters'),
        grossWeightUnitCode: Yup.string().nullable().test('required', requiredMessage, val => !!val),
        goodsItemNetWeight: Yup.string().nullable()
          .test('is-whole', 'Must be a whole number (no decimals)', val => !val || /^[0-9]+$/.test(val)),
        volume: Yup.string().nullable()
          .test('is-whole', 'Must be a whole number (no decimals)', val => !val || /^[0-9]+$/.test(val)),
        goodsItemDescription: Yup.string().nullable().max(2560, 'Max 2560 characters')
          .test('goods-desc-required', requiredMessage, function(val) {
            const root = this.from && this.from[this.from.length - 1]
            const documentType = root && root.value && root.value.documentType
            return documentType !== '714' || !!val
          }),
        additionalInformationRelatedToGoodsItem: Yup.string().nullable().max(512, 'Max 512 characters'),
        handlingInstructionDescriptionCode: Yup.string().nullable(),
        handlingInstructionDescription: Yup.string().nullable().max(256, 'Max 256 characters'),
        doesPackageToBeSplit: Yup.string().nullable(),
        locationName: Yup.string().nullable().max(256, 'Max 256 characters'),
        marksAndLabels: Yup.string().nullable().max(256, 'Max 256 characters'),
        netWeightUnitCode: Yup.string().nullable()
          .when('goodsItemNetWeight', {
            is: (val) => !!val,
            then: Yup.string().nullable().test('required', requiredMessage, val => !!val)
          }),
        volumeUnitCode: Yup.string().nullable()
          .when('volume', {
            is: (val) => !!val,
            then: Yup.string().nullable().test('required', requiredMessage, val => !!val)
          }),
        dangerousGoodsInfo: Yup.array().of(
          Yup.object({
            dangerousGoodsHazardClassCode: Yup.string().nullable().test('required', requiredMessage, val => !!val),
            unitedNationsDangerousGoodsIdentifier: Yup.string().nullable().test('required', requiredMessage, val => !!val).max(4, 'Max 4 characters'),
            dangerousGoodsFlashpointDescription: Yup.string().nullable().max(8, 'Max 8 characters'),
            shipmentFlashpointDegree: Yup.string().nullable()
              .test('is-numeric', 'Must be digits only', val => !val || /^[0-9]+$/.test(val))
              .max(3, 'Max 3 digits'),
            flashpointUnitCode: Yup.string().nullable()
              .when('shipmentFlashpointDegree', {
                is: (val) => !!val,
                then: Yup.string().nullable().test('required', requiredMessage, val => !!val)
              }),
            packagingDangerLevelCode: Yup.string().nullable()
          })
        ).nullable()
      })
    ).nullable().test('min-goods', 'At least one goods item is required', val => val != null && val.length >= 1),

    status: Yup.string().nullable()
  })
}

export default makeValidationSchema
